import type { SearchablePlan } from '@/api/planSearch'

interface Department {
  id: string
  code: string
  name: string
}

interface ClassItem {
  id: string
  code: string
  name: string
}

interface GeneratePlanNameParams {
  year?: number | null
  department?: Department | null
  classItem?: ClassItem | null
  subclass?: ClassItem | null
  sourcePlan?: SearchablePlan | null
  isFileSource?: boolean
}

/**
 * Extracts the numeric code from a hierarchy code string.
 * Strips level prefixes (e.g., "DEPT-0024" → "24", "CLS-0104" → "104")
 * and removes leading zeros.
 */
function extractCode(code: string): string {
  // Remove prefix like "DEPT-", "CLS-", "SUB-", etc.
  const numericPart = code.replace(/^[A-Z]+-/, '')
  // Remove leading zeros
  return numericPart.replace(/^0+/, '') || numericPart
}

/**
 * Extracts and formats the name portion from a hierarchy name string.
 * Names may be prefixed with numeric code (e.g., "24 - Ribbon" → "RIBBON").
 * Returns uppercase with no spaces.
 */
function extractName(name: string): string {
  // Remove leading numeric code prefix like "24 - " or "104 - "
  const cleaned = name.replace(/^\d+\s*-\s*/, '')
  return cleaned.replace(/\s+/g, '').toUpperCase()
}

/**
 * Generates a suggested plan name based on the provided parameters.
 *
 * Format: Year-DeptCode-DEPTNAME-ClassCode-CLASSNAME[-SubclassCode-SUBCLASSNAME]
 * Example: 2026-24-RIBBON-104-SEASONAL-1085-EASTERRIBBON
 *
 * @returns Generated suggestion or null if insufficient data
 */
export function generatePlanNameSuggestion(
  params: GeneratePlanNameParams
): string | null {
  const { year, department, classItem, subclass, sourcePlan, isFileSource } =
    params

  const currentYear = new Date().getFullYear()

  // For file-based historical data source
  if (isFileSource) {
    const yearPart = year || currentYear
    return `${yearPart}-HistoricalImport`
  }

  // For plan-based source (copy or historical data from plan)
  if (sourcePlan) {
    // Require both department and class to be selected
    if (!department || !classItem) {
      return null
    }

    const yearPart = year ?? currentYear
    const segments: (string | number)[] = [
      yearPart,
      extractCode(department.code),
      extractName(department.name),
      extractCode(classItem.code),
      extractName(classItem.name),
    ]

    if (subclass) {
      segments.push(extractCode(subclass.code), extractName(subclass.name))
    }

    return segments.join('-')
  }

  // For blank flow - need both department and class
  if (!department || !classItem) {
    return null
  }

  const yearPart = year || currentYear
  const segments: (string | number)[] = [
    yearPart,
    extractCode(department.code),
    extractName(department.name),
    extractCode(classItem.code),
    extractName(classItem.name),
  ]

  if (subclass) {
    segments.push(extractCode(subclass.code), extractName(subclass.name))
  }

  return segments.join('-')
}
